import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react'
import { User, Session, AuthError } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { authAuditService } from '../services/authAuditService'
import { UserLoggingService } from '../services/userLoggingService'

// Constants for authentication timeouts
const AUTH_TIMEOUT_MS = 15000 // 15 seconds timeout for auth requests

/**
 * Creates a timeout error that matches Supabase AuthError format
 */
const createTimeoutError = (): AuthError => {
  return new AuthError(
    'Request timed out. Please check your connection and try again.',
    408, // HTTP 408 Request Timeout
    'auth_timeout'
  )
}

/**
 * Wraps a promise with a timeout
 * @param promise The promise to wrap
 * @param timeoutMs Timeout in milliseconds
 * @returns Promise that rejects if timeout is reached
 */
const withTimeout = <T,>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(createTimeoutError())
      }, timeoutMs)
    })
  ])
}

type AuthContextType = {
  user: User | null
  loading: boolean
  session: Session | null
  isAdmin?: boolean
  isAdminLoading?: boolean
  adminPermissions?: string[]
  sendMagicLink: (email: string, redirectPath?: string) => Promise<{ data: any; error: any }>
  signInWithGoogle: (redirectPath?: string) => Promise<{ data: any; error: any }>
  signOut: () => Promise<void>
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<{ data: any; error: any }>
  signUp: (email: string, password: string, options?: any) => Promise<{ data: any; error: any }>
  resetPassword: (email: string) => Promise<{ data: any; error: any }>
  refreshSession: () => Promise<{ success: boolean; error?: string }>
  isSessionValid: () => Promise<boolean>
}

const getSafeRedirectPath = (redirectPath?: string) => {
  if (!redirectPath || !redirectPath.startsWith('/')) {
    return '/'
  }

  return redirectPath
}

/**
 * Absolute URL of this app's `/auth/callback`, used as the redirect target for
 * Google OAuth, sign-up confirmation, magic links and password resets.
 *
 * This must be the origin the app is actually served from. It previously
 * preferred `REACT_APP_SITE_URL`, which is set to the marketing site
 * (https://lookal.tech) — that host answers 404 on /auth/callback, so every one
 * of those links landed on a dead page. `window.location.origin` is correct on
 * any host (app.lookal.tech, a Vercel preview, or localhost), so it is used
 * unconditionally; an override can only reintroduce the same bug.
 */
const buildAuthCallbackUrl = (redirectPath?: string) => {
  const nextPath = getSafeRedirectPath(redirectPath)

  const origin = typeof window !== 'undefined' ? window.location.origin : ''

  if (!origin) {
    return `/auth/callback?next=${encodeURIComponent(nextPath)}`
  }

  // Strip trailing slash so we don't produce double slashes
  const base = origin.replace(/\/$/, '')

  return `${base}/auth/callback?next=${encodeURIComponent(nextPath)}`
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  const [isAdminLoading, setIsAdminLoading] = useState(false)
  const [adminPermissions, setAdminPermissions] = useState<string[]>([])
  // Keep a ref to the latest user so auth callbacks can compare and avoid
  // reacting to repeated identical events (Supabase triggers cross-tab sync
  // and token refresh events that may re-emit SIGNED_IN).
  const userRef = useRef<User | null>(null);
  // Store Remember Me preference to control session refresh behavior
  const rememberMeRef = useRef<boolean>(false);

  const syncAdminState = useCallback(async (baseUser: User): Promise<User> => {
    const requestUserId = baseUser.id;
    setIsAdminLoading(true);

    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', baseUser.id)
        .single();

      const nextIsAdmin = profile?.is_admin === true;

      if (userRef.current?.id === requestUserId) {
        setIsAdmin(nextIsAdmin);
      }

      // Fetch the caller's granted admin section permissions (empty for
      // superadmins, who bypass via is_admin; populated for role-based admins).
      let nextPermissions: string[] = [];
      try {
        const { data: perms } = await supabase.rpc('get_my_admin_permissions');
        // The RPC returns a JSONB array directly (jsonb_agg). Return as-is.
        if (Array.isArray(perms)) nextPermissions = perms as string[];
        else if (perms && typeof perms === 'object') nextPermissions = [perms as string];
      } catch {
        // Non-fatal: fall back to empty permissions.
      }
      if (userRef.current?.id === requestUserId) {
        setAdminPermissions(nextPermissions);
      }

      return {
        ...baseUser,
        user_metadata: {
          ...baseUser.user_metadata,
          is_admin: nextIsAdmin,
          role: nextIsAdmin ? 'admin' : 'user'
        },
        app_metadata: {
          ...baseUser.app_metadata,
          is_admin: nextIsAdmin,
          role: nextIsAdmin ? 'admin' : 'user'
        }
      };
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching admin status:', err);
      }

      if (userRef.current?.id === requestUserId) {
        setIsAdmin(false);
      }

      return {
        ...baseUser,
        user_metadata: {
          ...baseUser.user_metadata,
          is_admin: false,
          role: 'user'
        },
        app_metadata: {
          ...baseUser.app_metadata,
          is_admin: false,
          role: 'user'
        }
      };
    } finally {
      if (userRef.current?.id === requestUserId) {
        setIsAdminLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const applySessionState = (nextSession: Session | null) => {
      const nextUser = nextSession?.user ?? null;
      setUser(nextUser);
      setSession(nextSession);
      userRef.current = nextUser;

      if (nextUser) {
        setIsAdminLoading(true);
      } else {
        setIsAdmin(false);
        setIsAdminLoading(false);
      }
    };

    const getInitialSession = async () => {
      try {
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise<null>((resolve) => {
          setTimeout(() => {
            resolve(null);
          }, 3000);
        });

        const raced = await Promise.race([
          sessionPromise.then((res) => res),
          timeoutPromise
        ]);

        // If we timed out, don't block app startup. Apply session when it eventually resolves.
        if (raced === null) {
          setLoading(false);

          sessionPromise
            .then(async ({ data: { session } }) => {
              applySessionState(session ?? null);

              if (session?.user) {
                const enrichedUser = await syncAdminState(session.user);
                // Only update if still same user
                if (userRef.current?.id === session.user.id) {
                  setUser(enrichedUser);
                  userRef.current = enrichedUser;
                }
              }
            })
            .catch((err) => {
              if (process.env.NODE_ENV === 'development') {
                console.error('Failed to get session after timeout:', err);
              }
            });

          return;
        }

        const { data: { session } } = raced;

        // Set base session/user immediately
        applySessionState(session ?? null);

        // Enrich user metadata in background (do not block rendering)
        if (session?.user) {
          syncAdminState(session.user).then((enrichedUser) => {
            if (userRef.current?.id === session.user.id) {
              setUser(enrichedUser);
              userRef.current = enrichedUser;
            }
          });
        }

        // Load Remember Me preference from localStorage
        const savedRememberMe = localStorage.getItem('lookal_remember_me');
        if (savedRememberMe) {
          rememberMeRef.current = savedRememberMe === 'true';
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to get initial session:', err);
        }
        applySessionState(null)
      } finally {
        setLoading(false)
      }
    }

    getInitialSession()

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        const previousUser = userRef.current;
        // If this is a SIGNED_IN for the same user we already have, skip
        // and avoid re-setting state unnecessarily. This prevents repeated
        // updates when tabs sync or tokens refresh.
        const sameUser = !!(session?.user?.id && userRef.current?.id && session.user.id === userRef.current.id);

        // Only update state if user actually changed, or if signing out
        if (!sameUser || event === 'SIGNED_OUT') {

          // Apply base auth state immediately
          applySessionState(session ?? null);
          setLoading(false);

          // Enrich admin status asynchronously (do not block auth state propagation)
          if (event === 'SIGNED_IN' && session?.user) {
            syncAdminState(session.user).then((enrichedUser) => {
              if (userRef.current?.id === session.user.id) {
                setUser(enrichedUser);
                userRef.current = enrichedUser;
              }
            });
          }

          if (event === 'SIGNED_IN') {
            const provider =
              session?.user?.app_metadata?.provider ||
              session?.user?.app_metadata?.providers?.[0] ||
              'email';

            authAuditService.logEvent({
              eventType: 'login',
              email: session?.user?.email ?? undefined,
              userId: session?.user?.id,
              details: {
                provider,
              },
            }).catch(err => {
              if (process.env.NODE_ENV === 'development') {
                console.error('Failed to write auth audit login event:', err);
              }
            });

            // Log successful login
            UserLoggingService.logLogin(provider).catch(err => {
              if (process.env.NODE_ENV === 'development') {
                console.error('Failed to log login:', err);
              }
            });
          } else if (event === 'SIGNED_OUT') {
            authAuditService.logEvent({
              eventType: 'logout',
              email: previousUser?.email ?? undefined,
              userId: previousUser?.id,
              details: {
                source: 'auth_state_change',
              },
            }).catch(err => {
              if (process.env.NODE_ENV === 'development') {
                console.error('Failed to write auth audit logout event:', err);
              }
            });

            // Log logout (note: this runs before user is cleared, so logging still works)
            UserLoggingService.logLogout().catch(err => {
              if (process.env.NODE_ENV === 'development') {
                console.error('Failed to log logout:', err);
              }
            });
            // Clear Remember Me preference on logout
            localStorage.removeItem('lookal_remember_me');
            rememberMeRef.current = false;
          }
        }
      }
    )

    return () => {
      if (subscription) subscription.unsubscribe()
    }
  }, [syncAdminState])

  const signOut = async () => {
    await supabase.auth.signOut()
  }

  const signInWithGoogle = async (redirectPath: string = '/') => {
    const redirectTo = buildAuthCallbackUrl(redirectPath)

    try {
      await authAuditService.logEvent({
        eventType: 'oauth_signin_started',
        details: {
          provider: 'google',
          redirect_to: redirectTo,
        },
      })

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectTo,
        },
      })

      if (error) {
        return { data: null, error }
      }

      if (data?.url) {
        window.location.href = data.url
        return { data: { url: data.url }, error: null }
      }

      return {
        data: null,
        error: new Error('Failed to get Google OAuth URL from Supabase.'),
      }
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Failed to start Google Sign-In'),
      }
    }
  }

  const signIn = async (email: string, password: string, rememberMe: boolean = true) => {
    // Store Remember Me preference
    localStorage.setItem('lookal_remember_me', String(rememberMe));
    rememberMeRef.current = rememberMe;

    try {
      // Wrap the signIn call with a timeout to prevent indefinite hanging
      const result = await withTimeout(
        supabase.auth.signInWithPassword({ email, password }),
        AUTH_TIMEOUT_MS
      );

      // If login successful and Remember Me is false, we'll handle session expiry differently
      // The session will still be stored by Supabase, but we won't auto-refresh
      if (result.data?.session && !rememberMe) {
        // For non-remember sessions, we could set a shorter expiry or just let Supabase handle it normally
        // The key difference is in how we handle TOKEN_REFRESHED events
      }

      return result;
    } catch (error) {
      // Handle timeout or other errors
      if (error instanceof AuthError && error.code === 'auth_timeout') {
        return {
          data: { user: null, session: null },
          error: error
        };
      }

      // Re-throw other errors to maintain existing error handling behavior
      throw error;
    }
  }

  const signUp = async (email: string, password: string, options?: any) => {
    try {
      return await withTimeout(
        supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: buildAuthCallbackUrl('/'),
            data: options || {}
          }
        }),
        AUTH_TIMEOUT_MS
      );
    } catch (error) {
      if (error instanceof AuthError && error.code === 'auth_timeout') {
        return {
          data: { user: null, session: null },
          error: error
        };
      }
      throw error;
    }
  }

  const sendMagicLink = async (email: string, redirectPath: string = '/') => {
    try {
      const result = await withTimeout(
        supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: buildAuthCallbackUrl(redirectPath),
            shouldCreateUser: false,
          },
        }),
        AUTH_TIMEOUT_MS
      )

      if (!result.error) {
        authAuditService.logEvent({
          eventType: 'magic_link_requested',
          email,
          details: {
            redirect_to: buildAuthCallbackUrl(redirectPath),
          },
        }).catch(err => {
          if (process.env.NODE_ENV === 'development') {
            console.error('Failed to write auth audit magic link event:', err);
          }
        })
      }

      return result
    } catch (error) {
      if (error instanceof AuthError && error.code === 'auth_timeout') {
        return {
          data: { user: null, session: null },
          error: error
        }
      }

      throw error
    }
  }

  const resetPassword = async (email: string) => {
    try {
      const result = await withTimeout(
        supabase.auth.resetPasswordForEmail(email),
        AUTH_TIMEOUT_MS
      );

      // Log password reset request if successful
      if (!result.error) {
        authAuditService.logEvent({
          eventType: 'password_reset_requested',
          email,
        }).catch(err => {
          if (process.env.NODE_ENV === 'development') {
            console.error('Failed to write auth audit password reset event:', err);
          }
        });

        UserLoggingService.logPasswordResetRequested(email).catch(err => {
          if (process.env.NODE_ENV === 'development') {
            console.error('Failed to log password reset request:', err);
          }
        });
      }

      return result;
    } catch (error) {
      if (error instanceof AuthError && error.code === 'auth_timeout') {
        return {
          data: {},
          error: error
        };
      }
      throw error;
    }
  }

  // Refresh the current session - useful before protected operations
  const refreshSession = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data, error } = await supabase.auth.refreshSession();

      if (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Session refresh failed:', error);
        }
        // If refresh fails and Remember Me was not checked, this is expected
        if (!rememberMeRef.current) {
          // Clear the invalid session
          await supabase.auth.signOut();
        }
        return { success: false, error: error.message };
      }

      // Update session state
      if (data.session) {
        if (data.session.user) {
          setIsAdminLoading(true);
        }
        setSession(data.session);
        setUser(data.session.user ?? null);
        userRef.current = data.session.user ?? null;

        if (data.session.user) {
          const enrichedUser = await syncAdminState(data.session.user);
          if (userRef.current?.id === data.session.user.id) {
            setUser(enrichedUser);
            userRef.current = enrichedUser;
          }
        } else {
          setIsAdmin(false);
          setIsAdminLoading(false);
        }
      }

      return { success: true };
    } catch (err: any) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Unexpected error refreshing session:', err);
      }
      return { success: false, error: err.message };
    }
  }, [syncAdminState]);

  // Check if the current session is valid (not expired)
  const isSessionValid = useCallback(async (): Promise<boolean> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        return false;
      }

      // Check if session has expired
      const now = Date.now();
      const expiresAt = session.expires_at ? session.expires_at * 1000 : null;

      if (expiresAt !== null && now >= expiresAt) {
        // Session expired, try to refresh if Remember Me is enabled
        if (rememberMeRef.current) {
          const refreshResult = await refreshSession();
          return refreshResult.success;
        }
        return false;
      }

      return true;
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error checking session validity:', err);
      }
      return false;
    }
  }, [refreshSession]);

  const value = {
    user,
    loading,
    session,
    isAdmin,
    isAdminLoading,
    adminPermissions,
    sendMagicLink,
    signInWithGoogle,
    signOut,
    signIn,
    signUp,
    resetPassword,
    refreshSession,
    isSessionValid,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
